import ContentPasteOff from '@mui/icons-material/ContentPasteOff';
import FlagIcon from '@mui/icons-material/Flag';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import Tooltip from '@mui/material/Tooltip';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import type { Row } from '@src/common/components/DraggableTable';
import {
  DraggableTable,
  type RowDefinition,
} from '@src/common/components/DraggableTable';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { ViewableDocumentShortcut } from '@src/common/components/ViewableDocumentShortcut';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import { AssignationPicker } from '@src/orders/components/AssignationPicker';
import { PermitActions } from '@src/orders/components/permitList/PermitActions';
import { PermitStatusBadge } from '@src/orders/components/PermitStatusBadge';
import type {
  OrderListing,
  PermitSummaryGroup,
} from '@src/orders/domain/OrderList';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { useTranslation } from 'react-i18next';

interface Props {
  disabled?: boolean;
  order: OrderListing;
  onAssign: () => void;
  onDrag: (updatedRows: Row[]) => void;
  highlightedPermit?: string;
}

export const PermitList = ({
  disabled,
  order,
  onDrag,
  onAssign,
  highlightedPermit,
}: Props) => {
  const { t } = useTranslation();

  return (
    <DraggableTable
      disabled={disabled}
      ariaLabelledBy={t('permits')}
      columnsName={[
        t('permitSummaries.type'),
        t('permitSummaries.jurisdiction'),
        t('permitSummaries.stateId'),
        t('permitSummaries.permit'),
        t('permitSummaries.status'),
        t('permitSummaries.effectiveDate'),
        t('permitSummaries.assigned'),
        '',
      ]}
      rowsContent={order.permitSummaryGroups.map((group) =>
        rowContentFromPermitGroup(order, group, onAssign),
      )}
      onContentReordered={onDrag}
      highlightedRow={highlightedPermit}
    />
  );
};

const rowContentFromPermitGroup = (
  order: OrderListing,
  group: PermitSummaryGroup,
  onAssign: () => void,
) => {
  return permitSummaryRow({
    order,
    summary: group.originalPermit,
    onAssign,
    subRows: group.subsequentPermits.map((subsequentPermit) =>
      permitSummaryRow({
        order,
        summary: subsequentPermit,
        onAssign,
      }),
    ),
  });
};

const permitSummaryRow = ({
  order,
  summary,
  onAssign,
  subRows,
}: {
  order: OrderListing;
  summary: PermitSummary;
  onAssign: () => void;
  subRows?: RowDefinition[];
}) => {
  const viewableDocument = {
    id: summary.statePermitDocument?.documentId,
    key: summary.statePermitDocument?.key,
  };
  return {
    id: summary.id,
    subRows,
    columnsContent: [
      <Type key={'type-' + order.id} summary={summary} />,
      summary.jurisdiction,
      summary.statePermitNumber ?? summary.stateId ?? '-',
      summary.statePermitDocument ? (
        <ViewableDocumentShortcut
          key={summary.statePermitDocument.key}
          document={viewableDocument}
          fontSize="medium"
        />
      ) : null,
      <PermitStatusBadge key={'badge-' + order.id} status={summary.status} />,
      summary.effectiveDate.formatUTCDate(),
      <AssignationPicker
        key={'picker-' + order.id}
        order={order}
        summary={summary}
        onAssign={onAssign}
      />,
      <PermitActions
        key={'actions-' + order.id}
        order={order}
        permit={summary}
      />,
    ],
  };
};

const Type = ({
  summary: { trigger, type, isPriority, hasPendingChanges, keptAsIs },
}: {
  summary: PermitSummary;
}) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const canDisplayActionRequired = !isClientUser(user) && hasPendingChanges;
  const canDisplayKeptAsIs = keptAsIs;
  const canDisplayIsPriority =
    !canDisplayActionRequired && !canDisplayKeptAsIs && isPriority;

  const component = (
    <Stack
      direction="row"
      alignItems="center"
      gap={theme.spacing[1]}
      className={
        canDisplayActionRequired
          ? actionRequired
          : isPriority
            ? error
            : undefined
      }
    >
      {trigger && (
        <Typography style={{ fontWeight: 500 }}>({trigger[0]})</Typography>
      )}
      {canDisplayIsPriority && <PriorityHigh />}
      {canDisplayKeptAsIs && <ContentPasteOff />}
      {canDisplayActionRequired && <FlagIcon />}
      {type}
    </Stack>
  );

  if (canDisplayActionRequired) {
    return (
      <Tooltip title={t('permitSummaries.actionRequired')} placement="top">
        {component}
      </Tooltip>
    );
  } else if (canDisplayIsPriority) {
    return (
      <Tooltip title={t('permitSummaries.priorityPermit')} placement="top">
        {component}
      </Tooltip>
    );
  } else if (canDisplayKeptAsIs) {
    return (
      <Tooltip title={t('permitSummaries.rejectedChanges')} placement="top">
        {component}
      </Tooltip>
    );
  } else {
    return component;
  }
};

const error = css(() => ({
  color: colors.error['40'],
}));

const actionRequired = css(() => ({
  color: colors.tertiary['60'],
}));
